import { DesirabilityStrings } from '../../string-resources';
import offerlistingStatuses from '../../enums/offerlisting-statuses';

const LOW_DESIRABILITY = 'LOW_DESIRABILITY';

export interface DesirabilityDimension {
  /**
   * From graphql-server, which widens the raw payload's status: never null, always one of
   * LOW_DESIRABILITY | NOT_TRIGGERED | RULE_MISSING | ACTUAL_UNAVAILABLE. Only the first renders.
   */
  status?: string | null;
  actual?: number | null;
}

export interface Desirability {
  assessment?: string | null;
  shelfLife?: DesirabilityDimension | null;
  overlap?: DesirabilityDimension | null;
  cycleIndex?: DesirabilityDimension | null;
}

export interface DesirabilityReason {
  key: string;
  label: string;
  value: string;
}

const plural = (count: number, singular: string) => `${count} ${singular}${Math.abs(count) === 1 ? '' : 's'}`;

/**
 * Design order, deliberately not the model's. The dbt model's own low_desirability_reasons is
 * alphabetical, which is an artifact of STRING_AGG rather than a product decision.
 */
const REASONS: { key: keyof Desirability; label: string; format: (actual: number) => string }[] = [
  { key: 'shelfLife', label: DesirabilityStrings.shortDated, format: (actual) => plural(actual, 'day') },
  { key: 'cycleIndex', label: DesirabilityStrings.listedRepeatedly, format: (actual) => plural(actual, 'time') },
  {
    key: 'overlap',
    label: DesirabilityStrings.competingInventory,
    // overlap is a multiple of the supplier's own quantity. Five categories publish a break point
    // of 1, so "1x qty" is reachable and reads badly.
    format: (actual) => (actual === 1 ? 'same qty + more shelf life' : `${actual}x qty + more shelf life`),
  },
];

/** A type guard, so callers get narrowing rather than a non-null assertion. */
export function isDesirabilityFlagged(desirability?: Desirability | null): desirability is Desirability {
  return desirability?.assessment === LOW_DESIRABILITY;
}

/** Only the fields the Opportunity test reads, so callers are not forced to hold a full OfferListing. */
export interface OpportunityListing {
  status?: string | null;
  suggestions?: {
    reservePriceException?: {
      suggested?: boolean | null;
    } | null;
  } | null;
}

/**
 * Whether an inventory has at least one Opportunity: a listing the engine has suggested for a
 * reserve price exception and that is still actionable. Mirrors the `reservePriceExceptions` filter
 * in offer-listings.tsx -- an already-awarded listing is nothing the supplier can act on, so it
 * must not keep the indicator alive.
 */
export function hasReservePriceExceptionOpportunity(offerListings?: OpportunityListing[] | null): boolean {
  if (!Array.isArray(offerListings)) return false;
  return offerListings.some((listing) => !!listing?.suggestions?.reservePriceException?.suggested && listing.status !== offerlistingStatuses.AWARDED);
}

export function desirabilityReasons(desirability?: Desirability | null): DesirabilityReason[] {
  if (!isDesirabilityFlagged(desirability)) return [];
  // Captured to a const: TS discards narrowing of a parameter inside a callback, since a
  // parameter could in principle be reassigned before the callback runs.
  const flagged = desirability;
  return REASONS.reduce<DesirabilityReason[]>((reasons, { key, label, format }) => {
    const dimension = flagged[key] as DesirabilityDimension | null | undefined;
    if (dimension?.status !== LOW_DESIRABILITY) return reasons;
    // A tripped dimension with no actual should not print "null days". The model can only produce
    // this if its grain changes, but the indicator must degrade to silence rather than nonsense.
    if (typeof dimension.actual !== 'number') return reasons;
    reasons.push({ key: String(key), label, value: format(Math.round(dimension.actual)) });
    return reasons;
  }, []);
}

/** Splices a column before the first entry whose `field` matches, or prepends if none does. */
export function insertColumnBefore<T extends { field?: string }>(columns: T[], field: string, column: T): T[] {
  const next = [...columns];
  const index = next.findIndex((candidate) => candidate?.field === field);
  next.splice(index === -1 ? 0 : index, 0, column);
  return next;
}
